import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { signIn, signUp, confirmSignIn, fetchAuthSession, signOut, getCurrentUser } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import apiClientFactory from 'aws-api-gateway-client';
import {
  REST_API_URL,
  AMPLIFY_AUTH_AUTHENTICATION_FLOW_TYPE,
  AMPLIFY_AUTH_REGION,
  AMPLIFY_AUTH_USER_POOL_ID,
  AMPLIFY_AUTH_USER_POOL_WEB_CLIENT_ID,
  BASE_DASHBOARD_URL,
} from '../config';
import { AWS_SIGNED_IN_USER_SESSION_INVALID, CHECK_USER_AUTHENTICATED_ERROR } from '../constants/errorMesages';
import { SSO_LOGIN_ACCOUNT_TYPE, AUTH_RESULT_STATUS, AUTH_TOKEN_UNAVAILABLE_CODE } from '../constants/apiConstant';
import { checkIsWebApp } from '../services';

Amplify.configure({
  Auth: {
    Cognito: {
      region: AMPLIFY_AUTH_REGION,
      userPoolId: AMPLIFY_AUTH_USER_POOL_ID,
      userPoolClientId: AMPLIFY_AUTH_USER_POOL_WEB_CLIENT_ID,
    },
  },
});

const config = {
  invokeUrl: REST_API_URL,
};

const s3Config = {
  invokeUrl: BASE_DASHBOARD_URL,
};

export const signInUser = ({ login, accountType }) => {
  const loginPrefix = accountType === SSO_LOGIN_ACCOUNT_TYPE ? 'sso_' : ''; // to distinguish SSO and others
  const enhancedLogin = loginPrefix + login;

  const signInParams = {
    username: enhancedLogin,
    options: {
      authFlowType: AMPLIFY_AUTH_AUTHENTICATION_FLOW_TYPE,
    },
  };
  const signUpParams = {
    username: enhancedLogin,
    password: enhancedLogin,
    options: {
      userAttributes: {
        'custom:account_type': accountType,
        'custom:id': enhancedLogin,
      },
    },
  };

  const signInOrSignUp = async () => {
    try {
      return await signIn(signInParams);
    } catch (e) {
      if (e.name === 'UserNotFoundException') {
        // todo: !change password to uuid
        await signUp(signUpParams);
        return signIn(signInParams);
      }
      throw e;
    }
  };

  const handleSignIn = async () => {
    try {
      return await signInOrSignUp();
    } catch (e) {
      if (e.name === 'UserAlreadyAuthenticatedException') {
        await signOut();
        return signInOrSignUp();
      }
      throw e;
    }
  };

  return handleSignIn().catch(async (e) => {
    try {
      await signOut();
    } catch {
      // DO NOTHING
    }
    throw e;
  });
};

const validateSignedIn = async (result) => {
  if (!result?.isSignedIn) {
    throw Error(AWS_SIGNED_IN_USER_SESSION_INVALID);
  }
  const session = await fetchAuthSession();
  if (!session.tokens?.accessToken) {
    throw Error(AWS_SIGNED_IN_USER_SESSION_INVALID);
  }
  return session;
};

const confirmCustomSignIn = ({ challengeResponse }) =>
  confirmSignIn({
    challengeResponse,
  });

const isCustomChallenge = (result) => result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE';

const getChallengeType = (result) => result.nextStep?.additionalInfo?.challengeType;

// Recursively process custom challenges because each challenge response
// may trigger the next custom challenge.
const resolveCustomChallenge = async ({ result, challengeResponse }) => {
  if (!isCustomChallenge(result)) {
    return result; // No further custom challenge. Authentication complete.
  }
  const challengeType = getChallengeType(result);
  switch (challengeType) {
    case 'PASSWORD':
    case 'SSO_TOKEN':
    case 'PASSKEY_TOKEN':
      return resolveCustomChallenge({
        result: await confirmCustomSignIn({ challengeResponse }),
        challengeResponse,
      });
    case 'OTP_CODE':
      return {
        status: AUTH_RESULT_STATUS.OTP_REQUIRED,
        additionalInfo: result.nextStep.additionalInfo,
      };
    default:
      throw Error(AWS_SIGNED_IN_USER_SESSION_INVALID);
  }
};

export const handleCustomChallenge = async ({ result, challengeResponse }) => {
  try {
    const currentResult = await resolveCustomChallenge({ result, challengeResponse });
    if (currentResult.status === AUTH_RESULT_STATUS.OTP_REQUIRED) {
      return currentResult;
    }
    await validateSignedIn(currentResult);
    return {
      status: AUTH_RESULT_STATUS.SIGNED_IN,
      result: currentResult,
    };
  } catch (e) {
    try {
      await signOut();
    } catch {
      // DO NOTHING
    }
    throw e;
  }
};

export const verifyOtpChallenge = async ({ otpCode }) => {
  try {
    const result = await confirmCustomSignIn({
      challengeResponse: otpCode,
    });
    if (isCustomChallenge(result)) {
      const challengeType = getChallengeType(result);
      if (challengeType === 'OTP_CODE') {
        return {
          status: AUTH_RESULT_STATUS.OTP_REQUIRED,
          additionalInfo: result.nextStep.additionalInfo,
        };
      }
      throw Error(AWS_SIGNED_IN_USER_SESSION_INVALID);
    }

    await validateSignedIn(result);
    return {
      status: AUTH_RESULT_STATUS.SIGNED_IN,
      result,
    };
  } catch (e) {
    try {
      await signOut();
    } catch {
      // DO NOTHING
    }
    throw e;
  }
};

export const checkAuthenticatedUser = () =>
  getCurrentUser()
    .then((user) =>
      fetchAuthSession({ forceRefresh: true }).then((session) => {
        if (session.tokens?.accessToken) {
          return user.username;
        }
        throw Error();
      }),
    )
    .catch(async () => {
      try {
        await signOut();
      } catch {
        // DO NOTHING
      }
      throw Error(CHECK_USER_AUTHENTICATED_ERROR);
    });

export const logoutUser = async () => {
  await signOut();
};

export const get = 'GET';
export const post = 'POST';
export const patch = 'PATCH';
export const deleteMethod = 'DELETE';

export const PAGE_SIZE = 'pageSize=20';
export const PAGE_SIZE_WITHOUT_PAGINATION = 'pageSize=1000';

const createAuthTokenUnavailableError = (message) => {
  const error = new Error(message);
  error.code = AUTH_TOKEN_UNAVAILABLE_CODE;
  return error;
};

export const getIdToken = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw createAuthTokenUnavailableError('ID token is not available');
  }
  return idToken;
};

export const getAccessToken = async () => {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw createAuthTokenUnavailableError('Access token is not available');
  }
  return accessToken;
};

export const getRefreshToken = async () => {
  // refresh token can't access in v6 (access via low-level/internal API)
  const authTokens = await cognitoUserPoolsTokenProvider.tokenOrchestrator.getTokenStore().loadTokens();
  const refreshToken = authTokens?.refreshToken?.toString();
  if (!refreshToken) {
    throw createAuthTokenUnavailableError('Refresh token is not available');
  }
  return refreshToken;
};

export const authContext = {
  isAuthenticated: false,
};

const createAdditionalParamsInternal = async () => {
  const idToken = await getIdToken();
  const accessToken = await getAccessToken();
  const refreshToken = await getRefreshToken();

  return {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'x-invast-id-token': idToken,
      'x-invast-refresh-token': refreshToken,
    },
  };
};

export const createAdditionalParams = async () => {
  if (!checkIsWebApp() && !authContext.isAuthenticated) {
    return {
      headers: {},
    };
  }
  const params = await createAdditionalParamsInternal();
  return params;
};

const apiClient = apiClientFactory.newClient(config);

const s3Client = apiClientFactory.newClient(s3Config);

export const registerAuthEventListener = (updateAuthContext) => {
  return Hub.listen('auth', ({ payload }) => {
    switch (payload?.event) {
      case 'signedIn':
      case 'tokenRefresh':
        authContext.isAuthenticated = true;
        updateAuthContext({ isAuthenticated: true });
        break;
      case 'signedOut':
      case 'tokenRefresh_failure':
      case 'signInWithRedirect_failure':
        authContext.isAuthenticated = false;
        updateAuthContext(null);
        break;
      default:
        break;
    }
  });
};

export const refreshUserSession = async () => {
  const additionalParams = await createAdditionalParamsInternal();
  return apiClient.invokeApi({}, `register_tokens`, get, additionalParams);
};

export const invokeApi = (params, pathTemplate, ...rest) => {
  let path = pathTemplate;
  if (!checkIsWebApp() && !authContext.isAuthenticated) {
    if (!pathTemplate.startsWith('public/')) {
      path = `public/${pathTemplate}`;
    }
  }
  return apiClient.invokeApi(params, path, ...rest);
};

export const checkSession = async () => {
  // 暫定で、軽めのAPIを叩くことによりセッションチェックを行う
  const additionalParams = await createAdditionalParams();
  return invokeApi({}, `cart/grouped_items_count`, get, additionalParams);
};

export const fetchFile = (params, filePath, ...rest) => {
  return s3Client.invokeApi(params, filePath, ...rest);
};

export const passkeyChallenge = async () => {
  const result = await apiClient.invokeApi({}, `public/passkey/challenge`, get);
  if (result?.data?.code) {
    throw Object.assign(new Error(result.data.code), { code: result.data.code });
  }
  return result;
};

// TODO requestBodyをそのまま引数にしない
export const passkeyVerify = async ({ requestBody: { credential, token } }) => {
  const requestBody = { credential, token };
  const result = await apiClient.invokeApi({}, `public/passkey/verify`, post, {}, requestBody);
  if (result?.data?.code) {
    throw Object.assign(new Error(result.data.code), { code: result.data.code });
  }
  return result;
};
